// useVoiceInput — the reusable "dictate into a text field" hook.
//
// Records a short clip on an explicit tap, stops on an explicit tap (or a hard
// cap), transcribes it, and hands the plain text back via onTranscript. It is
// deliberately Dana-agnostic and NEVER auto-sends: the caller inserts the text
// into its existing input for the user to review, edit and manually submit.
//
// Privacy: the audio Blob lives only in memory for the duration of the call and
// is released as soon as it is transcribed; the mic stream is stopped on stop,
// cancel and unmount.
import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeAudio } from '../../../lib/voiceApi';
import { getApiErrorMessage } from '../../../lib/apiError';

export type VoiceInputState =
  | 'idle'
  | 'requesting'   // asking for the mic
  | 'recording'
  | 'processing'   // uploading + transcribing
  | 'error';

const MAX_MS = 60_000; // matches the server's 60 s / 5 MB cap
const MIN_BLOB_BYTES = 1000; // an accidental tap produces a near-empty clip

interface Options {
  onTranscript: (text: string) => void;
  onError?: (message: string) => void;
  locale?: string;
}

export interface UseVoiceInput {
  state: VoiceInputState;
  /** seconds elapsed while recording (for the timer UI) */
  elapsed: number;
  isBusy: boolean; // recording or processing
  start: () => Promise<void>;
  stop: () => void;   // finish → transcribe
  cancel: () => void; // discard, no transcription
  error: string | null;
}

export function useVoiceInput({ onTranscript, onError, locale }: Options): UseVoiceInput {
  const [state, setState] = useState<VoiceInputState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<number | null>(null);
  const capRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  const cleanupStream = useCallback(() => {
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
    if (capRef.current) { window.clearTimeout(capRef.current); capRef.current = null; }
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    recRef.current = null;
  }, []);

  const fail = useCallback((message: string) => {
    setError(message);
    setState('error');
    onError?.(message);
  }, [onError]);

  const start = useCallback(async () => {
    if (state === 'recording' || state === 'processing' || state === 'requesting') return;
    setError(null);
    cancelledRef.current = false;
    setState('requesting');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      fail('Voice input isn’t supported in this browser.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      fail('Microphone access is blocked — allow it in your browser and try again.');
      return;
    }
    streamRef.current = stream;
    const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      const durationMs = Date.now() - startedAtRef.current;
      let blob: Blob | null = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      chunksRef.current = [];
      cleanupStream();
      if (cancelledRef.current) { blob = null; setState('idle'); setElapsed(0); return; }
      if (blob.size < MIN_BLOB_BYTES) { blob = null; setState('idle'); setElapsed(0); return; }
      setState('processing');
      try {
        const res = await transcribeAudio(blob, blob.type, { locale, durationMs });
        if (res.text) onTranscript(res.text);
        setState('idle');
        setElapsed(0);
      } catch (e) {
        fail(getApiErrorMessage(e, 'Couldn’t transcribe that — please try again.'));
      } finally {
        blob = null; // never retained
      }
    };
    startedAtRef.current = Date.now();
    rec.start();
    recRef.current = rec;
    setElapsed(0);
    setState('recording');
    tickRef.current = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    capRef.current = window.setTimeout(() => {
      if (recRef.current === rec && rec.state === 'recording') rec.stop(); // → transcribe
    }, MAX_MS);
  }, [state, locale, onTranscript, fail, cleanupStream]);

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (rec && rec.state === 'recording') rec.stop();
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    const rec = recRef.current;
    if (rec && rec.state === 'recording') { rec.stop(); return; }
    cleanupStream();
    setState('idle');
    setElapsed(0);
  }, [cleanupStream]);

  // Stop the mic if the component unmounts mid-recording.
  useEffect(() => () => { cancelledRef.current = true; cleanupStream(); }, [cleanupStream]);

  return {
    state,
    elapsed,
    isBusy: state === 'recording' || state === 'processing' || state === 'requesting',
    start,
    stop,
    cancel,
    error,
  };
}
